// The one gate every panel's DM controls use (issues 1159, 1160). Its own module, beside the table state, so a test
// that stands a table state in still reads the real gate.
import { useCallback } from 'react';
import { ACT_POWER, type Need, type Power, type Role } from '@dragons-table/vtt-shared';
import { useTableState } from './TableState';
import { viewOnlyFor } from './viewOnly';

/**
 * Whether this viewer may send an act of `kind` as the DM's (issues 1159, 1160): the power `ACT_POWER` names for it,
 * the same table the server decides by. The DM (`powers` null, board role `dm`) may do any; a co-DM or guest DM only
 * what their powers hold, whichever board they see — and never what is the DM's alone; anyone else only what is
 * anyone's. A control that also needs the DM's board (a note pin, a cue on a hidden token) checks `role` beside it.
 */
export function mayActAs(role: Role, powers: readonly Power[] | null, kind: string): boolean {
  const need = ACT_POWER[kind];
  if (powers === null) return role === 'dm' || need === undefined;
  if (need === undefined) return true;
  return need !== 'dm' && powers.includes(need);
}

/** Whether this viewer holds `need`: the DM every one; a co-DM a power they were given, never `dm` (the DM's routes). */
export function holdsAs(role: Role, powers: readonly Power[] | null, need: Need): boolean {
  if (powers === null) return role === 'dm';
  return need !== 'dm' && powers.includes(need);
}

/** `mayActAs` for this table's viewer: one helper every panel gates its DM controls with. */
export function useMayAct(): (kind: string) => boolean {
  const { role, powers } = useTableState();
  return useCallback((kind: string) => mayActAs(role, powers ?? null, kind), [role, powers]);
}

/** `holdsAs` for this table's viewer: for a control that is a route's rather than an act's (`ROUTE_POWER`). */
export function useHolds(): (need: Need) => boolean {
  const { role, powers } = useTableState();
  return useCallback((need: Need) => holdsAs(role, powers ?? null, need), [role, powers]);
}

/** `viewOnlyFor` for this table's viewer (issue 1583): whether an act of `kind` would be refused as view-only, so a
 *  control is greyed with `VIEW_ONLY_REASON` before it is pressed rather than refused after. */
export function useViewOnlyFor(): (kind: string) => boolean {
  const { role, powers, status } = useTableState();
  return useCallback((kind: string) => viewOnlyFor(status.viewOnly, role, powers ?? null, kind), [status.viewOnly, role, powers]);
}
